import uiI18n from "../i18n";
import ListVacation from "../components/ListVacation";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LuMap, LuSearch, LuChevronRight } from "react-icons/lu";
import { POPULAR_DESTINATIONS } from "../data/popularDestinations";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import ListBiens from "../components/ListBiens";
import MobileFilterSheet from "../components/MobileFilterSheet";
import usePhoneLayout from "../hooks/usePhoneLayout";
import PropertyRequestHomeCta from "../components/propertyRequest/PropertyRequestHomeCta";
import SliderCards from "../components/SliderCards";

import {
  getOffers,
  getRents,
  getSales,
} from "../Services/biensService.jsx";

import { getVacations } from "../Services/vacationsService.jsx";
import citiesAndDelegations from "../data/citiesAndDelegations.js";
import { cityLabel, delegationLabel, isArabic, placeOptions } from "../utils/placeLabels";

import "../styles/animations.css";
import "../styles/home-desktop.css";
import HomeTunisiaMap from "../components/HomeTunisiaMap";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=2000&q=85";

const categories = [
  {
    get title() { return uiI18n.t("ui.buy"); },
    get subtitle() { return uiI18n.t("ui.houses_apartments"); },
    icon: "🏠",
    link: "/Category/sale",
  },
  {
    get title() { return uiI18n.t("ui.rent_308"); },
    get subtitle() { return uiI18n.t("ui.apartments_houses"); },
    icon: "🔑",
    link: "/Category/rent",
  },
  {
    get title() { return uiI18n.t("ui.holidays"); },
    get subtitle() { return uiI18n.t("ui.villas_residences"); },
    icon: "🏖️",
    link: "/vacation",
  },
  {
    get title() { return uiI18n.t("ui.offices"); },
    get subtitle() { return uiI18n.t("ui.business_spaces"); },
    icon: "🏢",
    link: "/offers?category=office",
  },
  {
    get title() { return uiI18n.t("ui.shops"); },
    get subtitle() { return uiI18n.t("ui.premises_businesses"); },
    icon: "🏪",
    link: "/offers?category=commercial",
  },
  {
    get title() { return uiI18n.t("ui.land"); },
    get subtitle() { return uiI18n.t("ui.land_for_sale"); },
    icon: "🌳",
    link: "/offers?category=land",
  },
];

const statistics = [
  {
    value: "18 000+",
    get label() { return uiI18n.t("ui.available_properties"); },
    icon: "⌂",
  },
  {
    value: "7 500+",
    get label() { return uiI18n.t("ui.satisfied_customers"); },
    icon: "♙",
  },
  {
    value: "24",
    get label() { return uiI18n.t("ui.governorates_covered"); },
    icon: "⌖",
  },
  {
    value: "4.9/5",
    get label() { return uiI18n.t("ui.average_rating"); },
    icon: "★",
  },
];

const advantages = [
  {
    get title() { return uiI18n.t("ui.verified_listings"); },
    get description() { return uiI18n.t("ui.all_listings_are_manually_reviewed"); },
    icon: "✓",
  },
  {
    get title() { return uiI18n.t("ui.secure_payment"); },
    get description() { return uiI18n.t("ui.your_transactions_are_protected_and_secure"); },
    icon: "🔒",
  },
  {
    get title() { return uiI18n.t("ui.support_7_days_a_week"); },
    get description() { return uiI18n.t("ui.our_team_is_available_to_help_you"); },
    icon: "🎧",
  },
  {
    get title() { return uiI18n.t("ui.certified_agents"); },
    get description() { return uiI18n.t("ui.real_estate_professionals_at_your_service"); },
    icon: "♙",
  },
];

const popularCities = [
  {
    name: "Tunis",
    get count() { return uiI18n.t("ui.property_count", { count: 1250 }); },
    image:
      "/images/destinations/tunis.png",
  },
  {
    name: "Sfax",
    get count() { return uiI18n.t("ui.property_count", { count: 980 }); },
    image:
      "/images/destinations/sfax.png",
  },
  {
    name: "Sidi Bou Saïd",
    labelKey: "homeDestinations.sidiBouSaid",
    searchCity: "Tunis",
    keyword: "Sidi Bou Saïd",
    get count() { return uiI18n.t("ui.property_count", { count: 875 }); },
    image:
      "/images/destinations/sidi-bou-said.png",
  },
  {
    name: "Nabeul",
    get count() { return uiI18n.t("ui.property_count", { count: 660 }); },
    image:
      "/images/destinations/nabeul.png",
  },
  {
    name: "Kairouan",
    get count() { return uiI18n.t("ui.property_count", { count: 540 }); },
    image:
      "/images/destinations/kairouan.png",
  },
  {
    name: "Béja",
    get count() { return uiI18n.t("ui.property_count", { count: 320 }); },
    image:
      "/images/destinations/beja.png",
  },
];

const testimonials = [
  {
    name: "Ahmed K.",
    city: "Sfax",
    get text() { return uiI18n.t("ui.i_found_my_apartment_in_just_two_days_fast_and_efficient_service"); },
    avatar: "AK",
  },
  {
    name: "Sara M.",
    city: "Tunis",
    get text() { return uiI18n.t("ui.the_site_is_very_easy_to_use_and_the_listings_are_high_quality"); },
    avatar: "SM",
  },
  {
    name: "Mehdi T.",
    city: "Sousse",
    get text() { return uiI18n.t("ui.thanks_to_the_lina_immo_team_for_their_professionalism_and_attentiveness"); },
    avatar: "MT",
  },
];

const normalizeCities = (data) => {
  if (Array.isArray(data)) {
    return data
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        return (
          item.city ||
          item.name ||
          item.governorate ||
          item.gouvernorat ||
          ""
        );
      })
      .filter(Boolean);
  }

  if (data && typeof data === "object") {
    return Object.keys(data);
  }

  return [];
};

const getDelegationsByCity = (data, city) => {
  if (!city) {
    return [];
  }

  if (Array.isArray(data)) {
    const selectedItem = data.find((item) => {
      const itemCity =
        item?.city ||
        item?.name ||
        item?.governorate ||
        item?.gouvernorat;

      return itemCity === city;
    });

    return (
      selectedItem?.delegations ||
      selectedItem?.delegation ||
      selectedItem?.areas ||
      []
    );
  }

  if (data && typeof data === "object") {
    const selectedCityData = data[city];

    if (Array.isArray(selectedCityData)) {
      return selectedCityData;
    }

    return (
      selectedCityData?.delegations ||
      selectedCityData?.areas ||
      []
    );
  }

  return [];
};

const SectionHeader = ({ title, link, linkLabel = uiI18n.t("ui.view_all") }) => (
  <div className="mb-5 flex items-end justify-between gap-4">
    <h2 className="text-2xl font-extrabold text-slate-900 md:text-3xl">
      {title}
    </h2>

    {link && (
      <Link
        to={link}
        className="hidden text-sm font-semibold text-slate-700 transition hover:text-green-600 sm:flex sm:items-center sm:gap-2"
      >
        {linkLabel}
        <span aria-hidden="true">›</span>
      </Link>
    )}
  </div>
);

const Home = () => {
  const { t, i18n } = useTranslation();

  const [mobileStatistics, setMobileStatistics] = useState(() => window.matchMedia("(max-width: 768px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const update = () => setMobileStatistics(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const statisticsBlock = (
      <section className="lina-home-stats relative z-10 mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {statistics.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 border-b border-slate-100 p-6 last:border-b-0 sm:border-r lg:border-b-0"
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-2xl font-black text-slate-900">
                {stat.icon}
              </div>

              <div>
                <strong className="block text-2xl font-black text-slate-900">
                  <bdi dir="ltr">{stat.value}</bdi>
                </strong>
                <span className="text-sm text-slate-500">
                  {stat.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
  );

  const [offerListings, setOfferListings] = useState([]);
  const [vacationListings, setVacationListings] = useState([]);
  const [rentListings, setRentListings] = useState([]);
  const [saleListings, setSaleListings] = useState([]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const phone = usePhoneLayout();
  const FilterContainer = phone ? MobileFilterSheet : React.Fragment;
  const [searchType, setSearchType] = useState("sale");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDelegation, setSelectedDelegation] = useState("");
  const [category, setCategory] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [libelle, setLibelle] = useState("");

  const cities = useMemo(
    () => normalizeCities(citiesAndDelegations),
    []
  );

  const delegations = useMemo(
    () =>
      getDelegationsByCity(
        citiesAndDelegations,
        selectedCity
      ),
    [selectedCity]
  );

  // Visible labels only: option values stay canonical (e.g. "Sahloul"). In Arabic the labels come
  // from the catalog and are sorted by label; French and English keep the names and order shown so far.
  const language = i18n.language;
  const arabic = isArabic(language);
  const cityOptions = useMemo(
    () => placeOptions(cities, (city) => (arabic ? cityLabel(city, language) : city), language),
    [cities, arabic, language]
  );

  const delegationOptions = useMemo(
    () =>
      placeOptions(
        delegations
          .map((delegation) =>
            typeof delegation === "string"
              ? delegation
              : delegation.name || delegation.delegation
          ),
        (delegation) => (arabic ? delegationLabel(selectedCity, delegation, language) : delegation),
        language
      ),
    [delegations, selectedCity, arabic, language]
  );

  const filters = useMemo(
    () => ({
      city: selectedCity,
      delegation: selectedDelegation,
      category,
      minPrice,
      maxPrice,
      libelle,
    }),
    [
      selectedCity,
      selectedDelegation,
      category,
      minPrice,
      maxPrice,
      libelle,
    ]
  );

  const loadHomepageData = async () => {
    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled([
        getOffers(0, {}),
        getRents(0, {}),
        getSales(0, {}),
        getVacations(0, {}),
      ]);

      const [offersResult, rentsResult, salesResult, vacationsResult] =
        results;

      if (offersResult.status === "fulfilled") {
        setOfferListings(offersResult.value || []);
      }

      if (rentsResult.status === "fulfilled") {
        setRentListings(rentsResult.value || []);
      }

      if (salesResult.status === "fulfilled") {
        setSaleListings(salesResult.value || []);
      }

      if (vacationsResult.status === "fulfilled") {
        setVacationListings(vacationsResult.value || []);
      }

      const allRequestsFailed = results.every(
        (result) => result.status === "rejected"
      );

      if (allRequestsFailed) {
        throw new Error(
          uiI18n.t("ui.unable_to_load_listings")
        );
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
          uiI18n.t("ui.loading_failed")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHomepageData();
  }, []);

  useEffect(() => {
    const elements =
      document.querySelectorAll(".fade-in");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [
    offerListings,
    rentListings,
    saleListings,
    vacationListings,
  ]);

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
    setSelectedDelegation("");
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    setHasSearched(true);
    setSearchLoading(true);
    setError("");

    try {
      if (searchType === "rent") {
        const response = await getRents(0, filters);
        setRentListings(response || []);
      } else if (searchType === "vacation") {
        const response = await getVacations(0, filters);
        setVacationListings(response || []);
      } else {
        const response = await getSales(0, filters);
        setSaleListings(response || []);
      }

      window.setTimeout(() => {
        document
          .getElementById("search-results")
          ?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }, 100);
    } catch (requestError) {
      setError(
        requestError?.message ||
          uiI18n.t("ui.search_failed")
      );
    } finally {
      setSearchLoading(false);
    }
  };

  // Only the desktop landing presentation is condensed. Submitted searches keep every existing result section.
  const showOriginalResults = mobileStatistics || hasSearched;
  const featured = [];
  const seen = new Set();
  const groups = [[offerListings, false], [rentListings, false], [vacationListings, true], [saleListings, false]];
  for (let index = 0; index < 4; index++) for (const [items, vacation] of groups) {
    const item = items[index];
    if (!item) continue;
    const key = (vacation ? "vacation:" : "bien:") + (item.id || item.ID);
    if (!seen.has(key) && featured.length < 4) { featured.push({ item, vacation, key }); seen.add(key); }
  }

  const renderListings = (
    title,
    listings,
    link,
    linkLabel
  ) => {
    if (!listings?.length) {
      return null;
    }

    return (
      <section className="fade-in">
        <SectionHeader
          title={title}
          link={link}
          linkLabel={linkLabel}
        />

<ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {listings.slice(0, 4).map((bien) => (
            <ListBiens
              key={bien.ID || bien.id}
              listing={bien}
              isHome
            />
          ))}
        </ul>

        <Link
          to={link}
          className="mt-5 flex items-center justify-center rounded-xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-800 transition hover:border-green-500 hover:text-green-600 sm:hidden"
        >
          {linkLabel}
        </Link>
      </section>
    );
  };

  return (
    <div className="lina-home min-h-screen bg-white text-slate-900">
      <Helmet>
        <title> {uiI18n.t("ui.houses_for_sale_rentals_and_villas_in_tunisia_lina_immo")} </title>

        <meta
          name="description"
          content={uiI18n.t("ui.explore_lina_immo_listings_houses_for_sale_apartments_for_rent_villas_land_and_vacation_rentals_in_t")}
        />

        <meta
          name="keywords"
          content={uiI18n.t("ui.tunisia_real_estate_house_for_sale_apartment_for_rent_villa_for_sale_land_vacation_rental_tunis_sfax")}
        />
      </Helmet>

      {/* HERO */}
      <section
        className="lina-home-hero relative min-h-[590px] bg-cover bg-center"
        style={{
          backgroundImage: `url("${HERO_IMAGE}")`,
        }}
      >
        <div className="lina-home-hero-shade absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/10" />

        <div className="lina-home-hero-inner relative mx-auto flex min-h-[590px] max-w-7xl items-center px-4 py-16 sm:px-6 lg:px-8">
          <div className="w-full">
            <div className="max-w-3xl">
              <p className="lina-home-eyebrow mb-4 inline-flex rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur"> {uiI18n.t("ui.your_real_estate_partner_in_tunisia")} </p>

              <h1 className="lina-home-title max-w-2xl text-4xl font-black leading-tight text-white sm:text-5xl lg:text-7xl"> {uiI18n.t("ui.find_your_next_home")} </h1>

              <p className="lina-home-lead mt-5 max-w-xl text-base leading-7 text-white/85 sm:text-lg"> {uiI18n.t("ui.buy_rent_or_book_the_property_that_meets_your_needs_anywhere_in_tunisia")} </p>
            </div>

            <form
              onSubmit={handleSearch}
                className="lina-location-fields lina-m-sheet lina-home-search mt-8 max-w-6xl overflow-hidden rounded-3xl bg-white p-4 shadow-2xl sm:p-6"
            >
              <div className="lina-home-types mb-5 flex flex-wrap gap-3 border-b border-slate-100 pb-4">
                {[
                  {
                    value: "sale",
                    label: uiI18n.t("ui.buy"),
                  },
                  {
                    value: "rent",
                    label: uiI18n.t("ui.rent_308"),
                  },
                  {
                    value: "vacation",
                    label: uiI18n.t("ui.holidays"),
                  },
                ].map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    aria-pressed={searchType === item.value}
                    onClick={() =>
                      setSearchType(item.value)
                    }
                    className={`rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                      searchType === item.value
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <FilterContainer {...(phone ? { firstLevel: <><select aria-label={t("ui.governorate")} value={selectedCity} onChange={handleCityChange}><option value="">{t("ui.all_governorates")}</option>{cityOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select><select aria-label={t("ui.property_type")} value={category} onChange={event => setCategory(event.target.value)}><option value=""> {uiI18n.t("ui.all_types")} </option>
                    <option value="apartment"> {uiI18n.t("ui.apartment")} </option>
                    <option value="house">{uiI18n.t("ui.house")}</option>
                    <option value="villa">{uiI18n.t("ui.villa")}</option>
                    <option value="office">{uiI18n.t("ui.office")}</option>
                    <option value="commercial"> {uiI18n.t("ui.shop")} </option>
                    <option value="land">{uiI18n.t("ui.land_508")}</option></select></>, summary: <div className="phone-home-controls"><button type="submit" aria-label={t("search_form.search")}><LuSearch aria-hidden="true" /></button><input aria-label={t("search_form.label")} placeholder={t("mobileFinal.searchPlaceholder")} value={libelle} onChange={e => setLibelle(e.target.value)} /></div> } : {})}>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500"> {uiI18n.t("ui.governorate")} </span>

                  <select
                    value={selectedCity}
                    onChange={handleCityChange}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                  >
                    <option value=""> {uiI18n.t("ui.all_governorates")} </option>

                    {cityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500"> {uiI18n.t("ui.delegation")} </span>

                  <select
                    value={selectedDelegation}
                    onChange={(event) =>
                      setSelectedDelegation(
                        event.target.value
                      )
                    }
                    disabled={!selectedCity}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none transition disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                  >
                    <option value=""> {uiI18n.t("ui.all_districts")} </option>

                    {delegationOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500"> {uiI18n.t("ui.property_type")} </span>

                  <select
                    value={category}
                    onChange={(event) =>
                      setCategory(event.target.value)
                    }
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                  >
                    <option value=""> {uiI18n.t("ui.all_types")} </option>
                    <option value="apartment"> {uiI18n.t("ui.apartment")} </option>
                    <option value="house">{uiI18n.t("ui.house")}</option>
                    <option value="villa">{uiI18n.t("ui.villa")}</option>
                    <option value="office">{uiI18n.t("ui.office")}</option>
                    <option value="commercial"> {uiI18n.t("ui.shop")} </option>
                    <option value="land">{uiI18n.t("ui.land_508")}</option>
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500"> {uiI18n.t("ui.maximum_price")} </span>

                  <input
                    type="number"
                    min="0"
                    value={maxPrice}
                    onChange={(event) =>
                      setMaxPrice(event.target.value)
                    }
                    placeholder={uiI18n.t("ui.all_budgets")}
                    className="h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                  />
                </label>

                <div className="flex items-end">
                  {phone && <button type="button" className="phone-filter-reset" onClick={() => { setSelectedCity(""); setSelectedDelegation(""); setCategory(""); setMaxPrice(""); setLibelle(""); }}>{t("mobileFinal.reset")}</button>}
                  <button
                    type="submit"
                    disabled={searchLoading}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-6 text-sm font-extrabold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 disabled:cursor-wait disabled:opacity-70"
                  >
                    <span aria-hidden="true">⌕</span>
                    {searchLoading
                      ? uiI18n.t("ui.searching")
                      : phone ? t("mobileFinal.apply") : uiI18n.t("ui.search")}
                  </button>
                </div>
              </div>
              </FilterContainer>
            </form>
            {phone && <><div id="phone-home-ai" /><Link className="phone-requests-card" to="/property-requests"><LuSearch aria-hidden="true" /><strong>{t("propertyRequest.nav.public")}</strong><small>{t("propertyRequest.nav.new")}</small><LuChevronRight aria-hidden="true" /></Link></>}

            {/* Mobile : destinations populaires (raccourcis vers les résultats filtrés). */}
            <nav className="lina-m-popular sm:hidden" aria-label={t("mobile.popular")}>
              <h2>{t("mobile.popular")}</h2>
              <div>
                {POPULAR_DESTINATIONS.map(({ label, city, delegation }) => {
                  const params = new URLSearchParams({ city });
                  if (delegation && searchType !== "vacation") params.set("delegation", delegation);
                  return (
                    <Link key={label} to={(searchType === "vacation" ? "/Vacation?" : `/Category/${searchType}?`) + params}>
                      {label}
                    </Link>
                  );
                })}
              </div>
            </nav>
            {/* Mobile : résultats directement sur la carte. */}
            <Link
              to={searchType === "vacation" ? "/Vacation?view=map" : `/Category/${searchType}?view=map`}
              className="lina-m-secondary sm:hidden"
            >
              <LuMap aria-hidden="true" size={20} />
              {t("mobile.viewMap")}
            </Link>

            <div className="lina-home-trust mt-5 flex items-center gap-2 text-sm font-semibold text-white">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
                ✓
              </span> {uiI18n.t("ui.over_12_000_listings_available")} </div>
          </div>
        </div>
      </section>

      {!mobileStatistics && <HomeTunisiaMap listings={[...offerListings, ...saleListings, ...rentListings, ...vacationListings]} href={searchType === "vacation" ? "/Vacation?view=map" : `/Category/${searchType}?view=map`} />}

      <main
        id="search-results"
        className="mx-auto max-w-7xl space-y-20 px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
      >
        {error && (
          <div
            role="alert"
            className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-700"
          >
            {error}
          </div>
        )}

        {/* JE RECHERCHE UN BIEN */}
        <PropertyRequestHomeCta />

        {/* CATÉGORIES */}
        <section className="fade-in phone-home-categories">
          <SectionHeader
            title={uiI18n.t("ui.explore_by_category")}
            link="/offers"
            linkLabel={uiI18n.t("ui.all_categories")}
          />

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {categories.map((item) => (
              <Link
                key={item.title}
                to={item.link}
                className="group rounded-3xl border border-slate-100 bg-slate-50 p-5 text-center transition duration-300 hover:-translate-y-1 hover:border-green-200 hover:bg-white hover:shadow-xl"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-3xl shadow-sm transition group-hover:scale-110">
                  {item.icon}
                </div>

                <h3 className="mt-4 font-extrabold text-slate-900">
                  {item.title}
                </h3>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {item.subtitle}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* ANNONCES */}
        {!showOriginalResults && !loading && featured.length > 0 && <section className="fade-in desktop-home-featured">
          <SectionHeader title={t("ui.featured_properties")} link="/offers" linkLabel={t("see_all_ads")} />
          <ul className="grid grid-cols-2 gap-5 xl:grid-cols-4">{featured.map(({item, vacation, key}) => vacation
            ? <ListVacation key={key} listing={item} isHome /> : <ListBiens key={key} listing={item} isHome />)}</ul>
        </section>}
        {loading ? (
          <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-[390px] animate-pulse rounded-3xl bg-slate-100"
              />
            ))}
          </section>
        ) : showOriginalResults && (
          <>
            {renderListings(
              uiI18n.t("ui.featured_properties"),
              offerListings,
              "/offers",
              t("see_all_offers") || uiI18n.t("ui.view_listings")
            )}

            {renderListings(
              t("for_rent") || uiI18n.t("ui.for_rent"),
              rentListings,
              "/Category/rent",
              t("see_all_ads") || uiI18n.t("ui.all_rentals")
            )}

            {renderListings(
              t("for_sale") || uiI18n.t("ui.for_sale"),
              saleListings,
              "/Category/sale",
              t("see_all_ads") || uiI18n.t("ui.all_sales")
            )}
          </>
        )}

        {/* LOCATIONS DE VACANCES */}
        {showOriginalResults && <section className="fade-in">
          <SectionHeader
            title={t("location_day") || uiI18n.t("ui.holiday_rental")}
            link="/vacation"
            linkLabel={t("see_all_ads") || uiI18n.t("ui.all_listings")}
          />

          {vacationListings.length > 0 ? (
            <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {vacationListings
                .slice(0, 4)
                .map((bien) => (
                  <ListVacation
                    key={bien.ID || bien.id}
                    listing={bien}
                    isHome
                  />
                ))}
            </ul>
          ) : (
            <SliderCards />
          )}
        </section>}

        {mobileStatistics && statisticsBlock}

        {/* AVANTAGES */}
        <section className="fade-in desktop-home-benefits">
          <SectionHeader title={uiI18n.t("ui.why_choose_lina_immo")} />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(mobileStatistics ? advantages : [
            { title: t("desktopHome.verified"), description: t("desktopHome.verifiedText"), icon: "✓" },
            { title: t("desktopHome.everywhere"), description: t("desktopHome.everywhereText"), icon: "⌖" },
            { title: t("desktopHome.simple"), description: t("desktopHome.simpleText"), icon: "♙" },
            { title: t("desktopHome.support"), description: t("desktopHome.supportText"), icon: "🎧" },
          ]).map((advantage) => (
              <article
                key={advantage.title}
                className="flex gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-green-50 text-xl font-black text-green-600">
                  {advantage.icon}
                </div>

                <div>
                  <h3 className="font-extrabold text-slate-900">
                    {advantage.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {advantage.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* VILLES */}
        <section className="fade-in desktop-home-cities">
          <SectionHeader
            title={uiI18n.t("ui.our_popular_cities")}
            link="/offers"
            linkLabel={uiI18n.t("ui.all_cities")}
          />

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {popularCities.map((city) => (
              <Link
                key={city.name}
                to={`/offers?city=${encodeURIComponent(
                  city.searchCity || city.name
                )}${city.keyword ? `&libelle=${encodeURIComponent(city.keyword)}` : ""}`}
                className="group relative h-40 overflow-hidden rounded-2xl"
              >
                <img
                  src={city.image}
                  alt={uiI18n.t("ui.properties_in_city", { city: t(city.labelKey || `cities.${city.name}`, city.name) })}
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />

                <div className="absolute bottom-0 start-0 p-4 text-white">
                  <h3 className="text-lg font-black">
                    {t(city.labelKey || `cities.${city.name}`, city.name)}
                  </h3>
                  <p className="text-xs text-white/80">
                    {mobileStatistics ? city.count : null}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* TÉMOIGNAGES */}
        <section className="fade-in desktop-home-testimonials">
          <SectionHeader title={uiI18n.t("ui.they_trust_us")} />

          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.map((testimonial) => (
              <article
                key={testimonial.name}
                className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm"
              >
                <div className="text-lg tracking-widest text-amber-400">
                  ★★★★★
                </div>

                <blockquote className="mt-4 min-h-20 text-sm leading-7 text-slate-600">
                  “{testimonial.text}”
                </blockquote>

                <div className="mt-6 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-sm font-black text-white">
                    {testimonial.avatar}
                  </div>

                  <div>
                    <strong className="block text-sm text-slate-900">
                      {testimonial.name}
                    </strong>
                    <span className="text-xs text-slate-500">
                      {t(`cities.${testimonial.city}`, testimonial.city)}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* APPLICATION */}
        <section className="fade-in desktop-home-app overflow-hidden rounded-3xl bg-gradient-to-r from-sky-100 to-blue-50 px-6 py-10 md:px-12">
          <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-green-600"> {uiI18n.t("ui.mobile_app")} </p>

              <h2 className="mt-3 text-3xl font-black text-slate-900 md:text-4xl"> {uiI18n.t("ui.take_lina_immo_everywhere")} </h2>

              <p className="mt-4 max-w-xl leading-7 text-slate-600"> {uiI18n.t("ui.find_your_favorite_listings_contact_owners_and_post_properties_from_your_phone")} </p>
            </div>

            <div className="flex flex-wrap gap-4 lg:justify-end">
              <div className="w-full rounded-xl bg-black px-6 py-4 text-sm text-white sm:w-auto sm:max-w-xs">
                <span className="block font-bold">Android · Google Play</span>
                <span className="mt-1 block leading-5 text-slate-300">{uiI18n.t("ui.android_pending_validation")}</span>
              </div>

              <div className="w-full rounded-xl bg-black px-6 py-4 text-sm text-white sm:w-auto sm:max-w-xs">
                <span className="block font-bold">iOS · App Store</span>
                <span className="mt-1 block leading-5 text-slate-300">{uiI18n.t("ui.ios_coming_soon")}</span>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default Home;
